import type { GeoMarkupStore } from '../index';
import type { ApiError } from '@/types/api';
import type { MapLayer, ArtifactVisibility } from '@/types/layers';

// ===== ATOMIC LAYER STATE SELECTORS =====

export const selectCustomLayers = (state: GeoMarkupStore): MapLayer[] =>
  state.mapLayers.items;

export const selectCustomLayersIsLoading = (state: GeoMarkupStore): boolean =>
  state.mapLayers.isLoading;

export const selectCustomLayersIsSyncing = (state: GeoMarkupStore): boolean =>
  state.mapLayers.isSyncing;

export const selectCustomLayersError = (
  state: GeoMarkupStore
): ApiError | null => state.mapLayers.error;

export const selectCustomLayerVisibility = (
  state: GeoMarkupStore
): Record<string, boolean> => state.mapLayers.customLayerVisibility;

// ===== DERIVED LAYER SELECTORS =====

let cachedItems: MapLayer[] | null = null;
let cachedVisibility: Record<string, boolean> | null = null;
let cachedVisible: MapLayer[] = [];

export const selectVisibleCustomLayers = (
  state: GeoMarkupStore
): MapLayer[] => {
  const { items, customLayerVisibility } = state.mapLayers;
  if (items === cachedItems && customLayerVisibility === cachedVisibility) {
    return cachedVisible;
  }
  cachedItems = items;
  cachedVisibility = customLayerVisibility;
  cachedVisible = items.filter(
    (layer) => customLayerVisibility[layer.id] ?? layer.isVisible
  );
  return cachedVisible;
};

export const selectIsCustomLayerVisible =
  (layerId: string) =>
  (state: GeoMarkupStore): boolean => {
    const override = state.mapLayers.customLayerVisibility[layerId];
    if (override !== undefined) return override;
    const layer = state.mapLayers.items.find((l) => l.id === layerId);
    return layer?.isVisible ?? true;
  };

// ===== ARTIFACT LAYER SELECTORS =====

export const selectArtifactLayerVisibility = (
  state: GeoMarkupStore
): ArtifactVisibility => state.mapLayers.artifactLayerVisibility;

export const selectArtifactTasksVisible = (state: GeoMarkupStore): boolean =>
  state.mapLayers.artifactLayerVisibility.tasks;

export const selectArtifactNotesVisible = (state: GeoMarkupStore): boolean =>
  state.mapLayers.artifactLayerVisibility.notes;

export const selectArtifactObstructionsVisible = (
  state: GeoMarkupStore
): boolean => state.mapLayers.artifactLayerVisibility.obstructions;

export const selectHasHiddenArtifactLayers = (state: GeoMarkupStore): boolean =>
  !state.mapLayers.artifactLayerVisibility.tasks ||
  !state.mapLayers.artifactLayerVisibility.notes ||
  !state.mapLayers.artifactLayerVisibility.obstructions;
